"use client";

import logger from '../../../utils/logger';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import requests from "../../../services/httpServices";
import { FaUpload, FaEllipsisV } from "react-icons/fa";
import { HiOutlinePlus } from "react-icons/hi2";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { TfiAngleLeft } from "react-icons/tfi";
import { useMeetingContext } from "../../../context/MeetingContext";
import ImageIcon from "../../../images/icons/image.svg";
import VideoIcon from "../../../images/icons/video.svg";
import DocumentIcon from "../../../images/icons/document.svg";
import ElementsIcon from "../../../images/icons/elements.svg";
import { uploadFiles, getFiles, importElement } from "../../../services/elementServices";
import { toast } from "react-toastify";
import folderService from "../../../services/folderServices";
import { VerticalTemplates } from "../../../services/Templates";
import { useEditCtaContext } from "../../../context/EditCtaContext";
import CtaPreviewModal from "../pages/elemets/cta/preview/CtaPreviewModal";
import { IoMdClose } from "react-icons/io";
import FolderIcon from "../../../images/icons/folder.svg";

const ElementView = () => {
  const {
    loading,
    setLoading,
    error,
    setError,
    folders,
    setSelectedCreateElement,
    setMediaFileName,
    setUpdateFolderId,
    setName,
    setUpdatePresetId,
    setWebsiteUpdateData,
    getFolders,
  } = useMeetingContext();

  const {
    setCurrentStep,
    setFormSteps,
    setPresetEditId,
    setSelectedTemplateKey,
    formData,
    setFormData,
  } = useEditCtaContext();

  const [files, setFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [cta, setCta] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewElementData, setPreviewElementData] = useState(null);
  const [showImportElementModal, setShowImportElementModal] = useState(false);
  const [newElementName, setNewElementName] = useState("");
  const [importFile, setImportFile] = useState(null);

  const navigate = useNavigate();

  const { folderId } = useParams();

  // fetch files for current folder from elementServices (stable callback)
  const fetchFiles = useCallback(
    async (fid) => {
      if (!fid) {
        setFiles([]);
        return [];
      }

      try {
        setLoading(true);
        setError(null);
        const res = await getFiles(fid);
        // axios-style response: res.data may contain the array
        // try common shapes: data.files or data.presets or data
        const list = res?.files || [];
        const finalList = Array.isArray(list) ? list : [];
        setFiles(finalList);
        return finalList;
      } catch (err) {
        logger("warn", 
          "Failed to fetch files, falling back to folder presets:",
          err
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, setFiles]
  );

  // Load folder contents when component mounts or folder changes
  useEffect(() => {
    getFolders();
  }, [folderId, getFolders]);

  // Fetch files for counts and lists when folderId changes
  useEffect(() => {
    fetchFiles(folderId);
  }, [folderId, fetchFiles]);

  useEffect(() => {
    folderService.getCtaByFolderId(folderId).then((cta) => {
      setCta(cta);
    });
  }, [folderId]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowMenu(null);
    };

    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showMenu]);

  const handleFileUpload = async (file) => {
    // form data
    const formData = new FormData();
    formData.append("image", file);
    try {
      setUploadingFile(file);
      setUploadProgress(0);
      await uploadFiles(formData, folderId);
      toast.success("File uploaded successfully");
      // refresh list
      await fetchFiles(folderId);
    } catch (err) {
      logger("error", "Upload failed:", err);
      // fallback to mock upload
      toast.error("Upload failed, used mock upload");
    } finally {
      setUploadingFile(null);
      setUploadProgress(0);
    }
  };

  // categorize files for display
  const categorized = React.useMemo(() => {
    const images = [];
    const videos = [];
    const documents = [];
    const websites = [];
    const others = [];

    (files || []).forEach((f) => {
      const t = (f.type || f.fileType || f.mimetype || "").toLowerCase();
      const name = (
        f.name ||
        f.filename ||
        f.settings?.filename ||
        ""
      ).toLowerCase();

      if (t.includes("image") || name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
        images.push(f);
      } else if (t.includes("video") || name.match(/\.(mp4|mov|webm|mkv)$/)) {
        videos.push(f);
      } else if (
        t.includes("pdf") ||
        name.match(/\.(pdf|ppt|pptx|doc|docx|xls|xlsx)$/) ||
        t.includes("document")
      ) {
        documents.push(f);
      } else if (t.includes("website") || f.type === "website" || f.url) {
        websites.push(f);
      } else {
        others.push(f);
      }
    });

    return { images, videos, documents, websites, others };
  }, [files]);

  // file input ref and handlers for click/drag-drop upload card
  const fileInputRef = useRef(null);

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCategoryClick = (category) => {
    if (!folderId) return;
    // navigate to /folder/{folderId}/{category}
    navigate(`/dashboard/fileview/${folderId}/${category}`);
  };

  const getPreviewImage = (ele) => {
    if (ele.type === "cta") {
      const displayStyle = ele.displayStyle || "";
      const settings = ele.settings ? JSON.parse(ele.settings) : {};
      const placement = settings.placement || "center";

      if (displayStyle.startsWith("vertical")) {
        return placement === "left"
          ? "/icons/BannerLeft.svg"
          : "/icons/BannerRight.svg";
      } else if (displayStyle.startsWith("horizontal")) {
        return placement === "top"
          ? "/icons/BannerTop.svg"
          : "/icons/BannerBottom.svg";
      } else if (displayStyle.startsWith("centered")) {
        return "/icons/BannerCentered.svg";
      }

      return (
        VerticalTemplates[ele.displayStyle]?.previewImage ||
        "/icons/BannerCentered.svg"
      );
    } else if (ele.type === "website") {
      return "/icons/Websites.svg";
    } else if (ele.type === "video") {
      return "/icons/Video.svg";
    } else if (ele.type === "document") {
      const filename = (ele.settings?.filename || ele.name || "").toLowerCase();

      if (filename.includes(".pdf") || filename.includes("pdf")) {
        return "/icons/SlidesPDFDOC.svg";
      } else if (
        filename.includes(".ppt") ||
        filename.includes(".pptx") ||
        filename.includes("presentation")
      ) {
        return "/icons/Slides PPT Presentation.svg";
      } else {
        return "/icons/SlidesPDFDOC.svg";
      }
    } else if (ele.type === "prompter") {
      return "/icons/captions.svg";
    }

    return "/icons/BannerCentered.svg";
  };

  // Derive current folder name for header
  const currentFolder = React.useMemo(() => {
    if (!Array.isArray(folders)) return null;
    return folders.find((f) => String(f._id) === String(folderId));
  }, [folders, folderId]);

  const setEditElement = (element) => {
    if (element.type === "video") {
      setSelectedCreateElement("media_file");
      setMediaFileName(element.settings.filename);
      setUpdateFolderId(element.folderId);
      setName(element.name);
      setUpdatePresetId(element._id);
    } else if (element.type === "document") {
      setSelectedCreateElement("document_file");
      setMediaFileName(element.settings.filename);
      setUpdateFolderId(element.folderId);
      setName(element.name);
      setUpdatePresetId(element._id);
    } else if (element.type === "website") {
      setSelectedCreateElement("website");
      setWebsiteUpdateData(element);
      setUpdatePresetId(element._id);
      navigate(`/dashboard/elements/edit-website/${folderId}`);
    } else if (element.type === "prompter") {
      setSelectedCreateElement("prompter");
      setUpdatePresetId(element._id);
      setName(element.name);
      navigate(`/dashboard/elemets/edit-prompter/${folderId}`, {
        replace: true,
      });
    } else {
      setPresetEditId(element._id);
      setSelectedTemplateKey(element.displayStyle);
      setFormData({
        ...formData,
        ...JSON.parse(element.settings),
      });
      setFormSteps(VerticalTemplates[element.displayStyle].steps);
      setCurrentStep(VerticalTemplates[element.displayStyle].steps[0]);
      navigate(`/dashboard/elemets/edit-cta/${folderId}`, { replace: true });
    }
  };

  const handleDelete = () => {
    if (deleteItemId) {
      requests.post(`/element/delete`, {
        id: deleteItemId,
      });
      getFolders();
      setShowDeleteModal(false);
      setDeleteItemId("");
      setSelectedCreateElement("");
      // remove element from array
      setCta(cta.filter((element) => element._id !== deleteItemId));
    }
  };

  const previewElement = (element) => {
    if (element.type === "cta") {
      setPreviewElementData(element);
      setShowPreviewModal(true);
    } else if (element.type === "website") {
      window.open(`https://${JSON.parse(element.settings).link}`, "_blank");
    } else if (element.type === "prompter") {
      setPreviewElementData(element);
      setShowPreviewModal(true);
    }
  };

  // Note: Export functionality to be implemented
  const exportElement = (element) => {
    if (!element) return;

    // Implement export functionality here
    logger("log", "Exporting element:", element);

    // Clean presets by removing _id and userId
    const { _id, id, userId, ...exportData } = element;

    logger("log", "Export Data:", exportData);

    // Convert object to formatted JSON
    const jsonData = JSON.stringify(exportData, null, 2);

    // Create a blob
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link
    const link = document.createElement("a");
    link.href = url;
    link.download = `${element.name || "folder"}-export.rlk`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Note: Import functionality to be implemented
  const handleImportElement = async () => {
    if (!importFile || !newElementName) {
      toast.error("Please provide both folder name and import file.");
      return;
    }
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("elementName", newElementName);
      formData.append("folderId", folderId);
      logger("log", 'formData', formData);

      const res = await importElement(formData);
      if (res.success) {
        toast.success("Element imported successfully.");

        folderService.getCtaByFolderId(folderId).then((cta) => {
          setCta(cta);
        });

        setShowImportElementModal(false);
      }
      
      logger("log", 'Import response:', res);

      // const response = await requests.post(`/element/import`, formData, {
      //   headers: {
      //     "Content-Type": "multipart/form-data",
      //   },
      // });
      setLoading(false);
      // getFolders();
    } catch (error) {
      setLoading(false);
      toast.error("Failed to import element.");
    }
  };

  return (
    <div className="h-screen mt-10 md:mt-0">
      <div className="w-full max-w-6xl mx-auto py-3 md:py-6 px-0 primary-card-background rounded-xl md:rounded-2xl h-[calc(100vh-100px)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6 px-3 md:px-6">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <span className="text-white text-base md:text-lg font-semibold whitespace-nowrap">
              ELEMENTS
            </span>
            <TfiAngleLeft className="text-white/80 text-sm md:text-base flex-shrink-0" />
            <span className="text-white/80 text-xs md:text-sm truncate">
              {currentFolder?.name || ""}
            </span>
          </div>
        </div>
        {/* Error Display */}
        {error && (
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm md:text-base">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-6 md:py-8">
            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-2 text-gray-400 text-sm md:text-base">
              Loading files...
            </p>
          </div>
        )}

        {showPreviewModal && (
          <CtaPreviewModal
            element={previewElementData}
            onClose={() => setShowPreviewModal(false)}
          />
        )}

        {/* Category Section */}
        <div className="mb-4 md:mb-6 px-3 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {/* Images Category */}
            <div
              onClick={() => handleCategoryClick("images")}
              className="bg-[#6F5CA4] flex gap-2 sm:gap-4 items-center rounded-lg p-2.5 md:p-2 cursor-pointer transition-colors"
            >
              <img
                src={ImageIcon}
                alt="Images"
                className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-xs md:text-sm">
                  Images
                </div>
                <div className="text-gray-300 text-xs md:text-sm">
                  {categorized.images.length} files
                </div>
              </div>
              <div className="ml-auto flex-shrink-0">
                <FaEllipsisV className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            </div>

            {/* Videos Category */}
            <div
              onClick={() => handleCategoryClick("videos")}
              className="bg-[#6F5CA4] flex gap-2 sm:gap-4 items-center rounded-lg p-2.5 md:p-2 cursor-pointer transition-colors"
            >
              <img
                src={VideoIcon}
                alt="Videos"
                className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-xs md:text-sm">
                  Videos
                </div>
                <div className="text-gray-300 text-xs md:text-sm">
                  {categorized.videos.length} files
                </div>
              </div>
              <div className="ml-auto flex-shrink-0">
                <FaEllipsisV className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            </div>

            {/* Slides & Documents Category */}
            <div
              onClick={() => handleCategoryClick("documents")}
              className="bg-[#6F5CA4] flex gap-2 sm:gap-4 items-center rounded-lg p-2.5 md:p-2 cursor-pointer transition-colors"
            >
              <img
                src={DocumentIcon}
                alt="Documents"
                className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-xs md:text-sm">
                  Slides & Documents
                </div>
                <div className="text-gray-300 text-xs md:text-sm">
                  {categorized.documents.length} files
                </div>
              </div>
              <div className="ml-auto flex-shrink-0">
                <FaEllipsisV className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            </div>

            {/* Upload a File Button */}
            <div
              className="rounded-lg p-2 cursor-pointer bg-[#5E5D60] transition-colors"
              onClick={openFilePicker}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="elements-file-input"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
              />

              <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 items-center justify-center h-full text-center border-2 border-dashed border-gray-500 p-2 md:p-3 rounded-lg min-h-[60px] md:min-h-auto">
                <div className="text-white font-medium text-xs md:text-sm">
                  Upload a File
                </div>
                <div>
                  <HiOutlinePlus
                    color="white"
                    className="w-4 h-4 md:w-5 md:h-6 text-gray-400"
                  />
                </div>
                <div className="text-xs">
                  <p className="text-white text-xs md:text-sm">Click Here or</p>
                  <p className="text-gray-400 text-xs md:text-sm">
                    Drag & Drop
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showImportElementModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#615B72] relative rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="absolute top-2 right-2 md:top-3 md:right-3">
                <button
                  onClick={() => setShowImportElementModal(false)}
                  className="p-1 rounded cursor-pointer"
                >
                  <IoMdClose
                    color="white"
                    size={20}
                    className="md:w-6 md:h-6"
                  />
                </button>
              </div>

              <div className="flex items-center justify-center mb-3 md:mb-4">
                <img
                  src={FolderIcon}
                  alt="Folder"
                  className="w-12 h-12 md:w-auto md:h-auto"
                />
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-base md:text-lg text-white font-bold mb-2">
                    Element Name *
                  </label>
                  <input
                    type="text"
                    value={newElementName}
                    onChange={(e) => setNewElementName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                    placeholder="Enter element name"
                  />
                </div>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-base md:text-lg text-white font-bold mb-2">
                    Import Element File *
                  </label>
                  <input
                    type="file"
                    accept=".rlk"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => setShowImportElementModal(false)}
                  className="w-full sm:flex-1 px-4 py-2 border border-red-150 text-red-150 font-bold rounded-lg transition-colors cursor-pointer text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportElement}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 rounded-lg transition-colors text-white font-bold cursor-pointer text-sm md:text-base"
                >
                  {loading ? "Creating..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Elements Section */}
        <div className="bg-black rounded-xl md:rounded-2xl py-3 md:py-4 sm:py-6 md:py-8 px-0 min-h-[300px] md:min-h-[400px]">
          <div className="flex flex-col sm:flex-row justify-between mb-4 gap-3 sm:gap-4 px-3 md:px-4 sm:px-6 md:px-8">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 md:w-7 md:h-7 sm:w-8 sm:h-8 border-2 border-white rounded flex items-center justify-center flex-shrink-0">
                <img
                  className="h-4 w-3 md:h-5 md:w-3 sm:h-6 sm:w-4"
                  alt="Folders"
                  src={ElementsIcon}
                />
              </div>
              <span className="text-white text-sm md:text-base sm:text-lg md:text-xl font-medium">
                ELEMENTS
              </span>
            </div>
            {cta.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                <button
                  onClick={() => setShowImportElementModal(true)}
                  className="bg-[#6F00F6] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center gap-2 sm:gap-3 transition-colors text-sm sm:text-base w-full sm:w-auto"
                >
                  <span>Import an Element</span>
                  <img
                    src="/icons/r-link.svg"
                    alt=""
                    className="w-4 h-4 sm:w-auto sm:h-auto"
                  />
                </button>

                <CreateElementButton
                  onClick={() => {
                    navigate(`/dashboard/elemets/${folderId}`);
                  }}
                />
              </div>
            )}
          </div>

          <div
            className={`${
              cta.length === 0
                ? "flex flex-col justify-center items-center"
                : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-1 md:gap-1.5"
            } min-h-[200px] md:min-h-[300px] px-3 md:px-4 sm:px-6 md:px-8`}
          >
            {cta.length === 0 && (
              <CreateElementButton
                onClick={() => {
                  navigate(`/dashboard/elemets/${folderId}`);
                }}
              />
            )}

            {cta.length > 0 &&
              cta.map((element) => (
                <div
                  key={element._id}
                  className={`relative group cursor-pointer p-0.5 md:p-0.5 rounded-lg transition-all duration-200 bg-[#ffffff1f] flex flex-col hover:bg-[#ffffff2f] border border-transparent hover:border-gray-600 min-h-[90px] md:min-h-[120px] mx-0 my-0.5 md:my-2`}
                  onClick={() => {
                    previewElement(element);
                  }}
                >
                  {/* File Thumbnail */}
                  <div className="aspect-square mb-0.5 md:mb-2 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center w-full p-6 md:p-6">
                    <img
                      src={getPreviewImage(element)}
                      alt={element.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  </div>

                  {/* File Name */}
                  <p className="text-sm sm:text-base md:text-[14px] text-white truncate text-center mt-auto mb-1 md:mb-2 px-1">
                    {element.name}
                  </p>

                  {/* 3-dot Menu Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(element._id);
                    }}
                    className="absolute top-1 md:top-2 right-1 md:right-2 p-1 bg-gray-700/90 hover:bg-gray-600/90 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-20"
                  >
                    <FaEllipsisV className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                  </button>

                  {/* Menu Dropdown */}
                  {showMenu === element._id && (
                    <div
                      className="absolute top-8 sm:top-10 right-0 w-32 sm:w-30 md:w-24 bg-[#2A2835] border border-gray-600 rounded-md shadow-lg z-50"
                      style={{
                        boxShadow:
                          "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      <div className="py-1">
                        {!["document", "video"].includes(element.type) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(null);
                              setEditElement(element);
                            }}
                            className="block w-full text-left px-4 sm:px-4 md:px-3 py-3 sm:py-2 md:py-1.5 text-sm sm:text-sm md:text-xs text-gray-300 hover:bg-gray-700 hover:text-white active:bg-gray-600"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMenu(null);
                            setShowDeleteModal(true);
                            setDeleteItemId(element._id);
                          }}
                          className="block w-full text-left px-4 sm:px-4 md:px-3 py-3 sm:py-2 md:py-1.5 text-sm sm:text-sm md:text-xs text-red-400 hover:bg-gray-700 hover:text-red-300 active:bg-gray-600"
                        >
                          Delete
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            exportElement(element);
                          }}
                          className="block w-full text-left px-4 sm:px-4 md:px-3 py-3 sm:py-2 md:py-1.5 text-sm sm:text-sm md:text-xs text-white hover:bg-gray-700 hover:text-white-400 active:bg-gray-600"
                        >
                          Export
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#00000038] text-white rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-semibold text-white">
                  Upload File
                </h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-1 hover:bg-gray-700 rounded text-white"
                >
                  <IoMdCloseCircleOutline className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-3 md:p-4 lg:p-6 text-center">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                    disabled={uploadingFile}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <FaUpload className="w-5 h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-gray-400 mb-2" />
                    <span className="text-white text-sm md:text-base">
                      {uploadingFile ? "Uploading..." : "Click to select file"}
                    </span>
                    <span className="text-gray-400 text-xs md:text-sm">
                      or drag and drop
                    </span>
                  </label>
                </div>

                {uploadingFile && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs md:text-sm text-gray-300">
                      <span className="truncate mr-2">
                        {uploadingFile.name}
                      </span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 md:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploadingFile}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 rounded-lg transition-colors text-white text-sm md:text-base"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 p-4">
            <div className="bg-gray-800 text-white rounded-lg p-4 md:p-6 w-full max-w-md border border-gray-700">
              <h3 className="text-base md:text-lg lg:text-xl font-bold mb-3 md:mb-4">
                Delete Element
              </h3>
              <p className="text-gray-200 mb-4 md:mb-6 text-sm md:text-base">
                Are you sure you want to delete this element? This action cannot
                be undone.
              </p>
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="w-full sm:w-auto px-4 py-2 text-gray-300 hover:text-white cursor-pointer hover:bg-gray-700 rounded-lg transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDelete();
                    setShowDeleteModal(false);
                  }}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 cursor-pointer text-white rounded-lg hover:bg-red-700 transition-colors text-sm md:text-base"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElementView;

const CreateElementButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="bg-[#6F00F6] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center gap-2 sm:gap-3 transition-colors text-sm sm:text-base w-full sm:w-auto"
    >
      <span>Create an Element</span>
      <img
        src="/icons/r-link.svg"
        alt=""
        className="w-4 h-4 sm:w-auto sm:h-auto"
      />
    </button>
  );
};
